import createAuthClient from "@adsk/identity-web-sdk"
import type { AuthClient } from "@adsk/identity-web-sdk"

/**
 * Structural view of the SDK's AuthClient that exposes the surface this module
 * actually uses, including the SDK-internal `_loginWithPopup` method.
 *
 * We can't intersect with the imported `AuthClient` class type because it has
 * private fields, which makes the intersection `never` from outside the class.
 * See autodeskLoginWithPopup below for why we bypass the public `loginWithPopup`.
 */
type AutodeskAuthClient = {
    buildAuthorizeUrl: AuthClient["buildAuthorizeUrl"]
    getIDTokenSilently: AuthClient["getIDTokenSilently"]
    logout: AuthClient["logout"]
    _loginWithPopup(codeResult: { code: string; state: string }, redirect_uri: string, options: { login_hint?: string }): Promise<string>
}

type AutodeskOAuthCallbackMessage = {
    type?: string
    code?: string
    state?: string
    error?: string
    error_description?: string
}

let _authClientPromise: Promise<AutodeskAuthClient> | null = null
let _clientId: string | null = null
let _env: string | null = null

const _ADSK_CLIENT_CONFIG_KEY = "autodesk_client_config"

/**
 * Return the cached auth client Promise, creating it if the config has changed.
 *
 * Stores a Promise (not the resolved client) so the Promise can be cached and
 * awaited multiple times without triggering duplicate createAuthClient calls.
 *
 * Parameters:
 * clientId (string): The APS SPA client ID registered in the Autodesk Developer Portal.
 * env (string): Target environment — "prd" for production, "stg" for staging.
 * email (string | undefined): Optional login hint to pre-fill the Autodesk sign-in form.
 *
 * Returns:
 * Promise<AutodeskAuthClient>: Resolves to the Autodesk Identity auth client instance.
 */
function getOrCreateAuthClientPromise(clientId: string, env: string, email?: string): Promise<AutodeskAuthClient> {
    if (!_authClientPromise || _clientId !== clientId || _env !== env) {
        _authClientPromise = (createAuthClient({
            client_id: clientId,
            redirect_uri: `${window.location.origin}/oauth/callback`,
            redirect_uri_popup: `${window.location.origin}/oauth/callback`,
            env,
            // The SDK appends 'openid' automatically, so the effective scope is
            // 'data:read profapi:core-std-profile:read openid'.
            // 'profapi:core-std-profile:read' is required by Autodesk for the
            // /userinfo endpoint to return the user's email and profile fields.
            scope: "data:read openid profapi:core-std-profile:read",
            cacheLocation: "localstorage",
            useRefreshTokens: true,
            login_hint: email,
        }) as unknown) as Promise<AutodeskAuthClient>
        _clientId = clientId
        _env = env
    }
    return _authClientPromise
}

/**
 * Pre-initialize the Autodesk auth client so it is ready before the user
 * clicks the login button.
 *
 * Call this as soon as the client_id and env are known (e.g., after login_check
 * confirms Autodesk is available). Because createAuthClient is async, calling
 * loginWithPopup immediately after awaiting it would cross an async boundary and
 * risk popup blocking. Pre-initializing here gives the Promise time to resolve
 * before the user interaction occurs.
 *
 * Parameters:
 * clientId (string): The APS SPA client ID.
 * env (string): Target environment — "prd" or "stg".
 */
export function initAutodeskAuthClient(clientId: string, env: string): void {
    getOrCreateAuthClientPromise(clientId, env).catch(() => {
        // Swallow init errors; the next interactive call will surface them.
    })
}

/**
 * Persist the Autodesk client configuration to localStorage so that
 * autodeskLogout() can revoke tokens even after a full page refresh.
 *
 * Call this immediately after a successful Autodesk login.
 *
 * Parameters:
 * clientId (string): The APS SPA client ID.
 * env (string): Target environment — "prd" or "stg".
 */
export function storeAutodeskClientConfig(clientId: string, env: string): void {
    try {
        localStorage.setItem(_ADSK_CLIENT_CONFIG_KEY, JSON.stringify({ clientId, env }))
    } catch {
        // Non-fatal: if localStorage is unavailable, logout will fall back to module state.
    }
}

/**
 * Revoke Autodesk tokens and clear the SDK's local state.
 *
 * Reads the stored client configuration from module state (available within
 * the same page session) or localStorage (available after a page refresh).
 * Calls client.logout({ localOnly: true }) which revokes the access token and
 * refresh token via Autodesk's /authentication/v2/revoke endpoint, then clears
 * all SDK localStorage keys.  The function is best-effort: any error is silently
 * swallowed so that the caller's logout flow (e.g. POST /api/v3/users/logout/)
 * always continues.  The stored config entry is removed from localStorage on
 * completion regardless of whether the SDK call succeeded.
 *
 * Returns:
 * Promise<void>
 */
export async function autodeskLogout(): Promise<void> {
    let clientId = _clientId
    let env = _env

    if (!clientId || !env) {
        try {
            const stored = localStorage.getItem(_ADSK_CLIENT_CONFIG_KEY)
            if (stored) {
                const parsed = JSON.parse(stored) as { clientId?: string; env?: string }
                clientId = parsed.clientId ?? null
                env = parsed.env ?? null
            }
        } catch {
            // Non-fatal: proceed without Autodesk SDK logout.
        }
    }

    if (!clientId || !env) {
        return
    }

    try {
        const client = await getOrCreateAuthClientPromise(clientId, env)
        await client.logout({ localOnly: true })
    } catch {
        // Non-fatal: Rhumbix logout continues regardless of Autodesk SDK errors.
    } finally {
        try {
            localStorage.removeItem(_ADSK_CLIENT_CONFIG_KEY)
        } catch {
            // Ignore localStorage errors.
        }
    }
}

/**
 * Open an Autodesk Identity popup, authenticate the user, and return the
 * resulting access and ID tokens for exchange with the Rhumbix backend.
 *
 * Builds the PKCE authorization URL using the SDK, opens the popup directly
 * (bypassing client.loginWithPopup), then waits for the /oauth/callback page
 * to send the auth code back via BroadcastChannel.
 *
 * client.loginWithPopup() cannot be used because Autodesk's authorization server
 * sets Cross-Origin-Opener-Policy: same-origin. This severs the window.open()
 * reference from the parent (popup.closed === true immediately), so the SDK's
 * polling interval rejects with PopupCancelledError after one second — before
 * the user has finished authenticating. BroadcastChannel is used instead of
 * postMessage because COOP also nulls window.opener in the popup.
 *
 * Parameters:
 * clientId (string): The APS SPA client ID from the backend availability response.
 * env (string): Target environment — "prd" for production, "stg" for staging.
 * email (string): Optional login hint to pre-fill the Autodesk sign-in form.
 *
 * Returns:
 * Promise<{accessToken: string, idToken: string|null}>: Resolves with the Autodesk
 *   access token and OIDC ID token on successful authentication.  The ID token is
 *   best-effort and may be null if the SDK cache is unavailable.
 */
export async function autodeskLoginWithPopup(clientId: string, env: string, email: string): Promise<{ accessToken: string; idToken: string | null }> {
    const client = await getOrCreateAuthClientPromise(clientId, env, email)

    const redirectUri = `${window.location.origin}/oauth/callback`

    // Generate a random state value for CSRF/replay protection.
    const stateBytes = new Uint8Array(32)
    crypto.getRandomValues(stateBytes)
    const stateIn = Array.from(stateBytes, b => b.toString(16).padStart(2, "0")).join("")

    const loginOptions: { login_hint?: string } = email ? { login_hint: email } : {}
    const urlOptions = { ...loginOptions, state: stateIn, redirect_uri: redirectUri }

    // buildAuthorizeUrl generates the PKCE code verifier, stores it in localStorage
    // under 'popup_pkce_code_verifier', and returns the full authorization URL.
    // We call this directly rather than going through loginWithPopup, so that we
    // can open the popup ourselves and avoid the SDK's closed-popup polling.
    const authUrl = await client.buildAuthorizeUrl(urlOptions, true, true)

    const popup = window.open(
        authUrl,
        "auth:authorize:popup",
        `left=${window.screenX + (window.innerWidth - 770) / 2},` + `top=${window.screenY + (window.innerHeight - 770) / 2},` + "width=770,height=770,resizable,scrollbars=yes,status=1"
    )
    if (popup === null) {
        throw new Error("The authentication popup was blocked. Please allow popups for this site.")
    }

    // Wait for the /oauth/callback page to broadcast the auth code and state.
    // The callback page calls window.close() after posting, so the popup self-closes.
    // A 15-minute timeout matches the SDK's DEFAULT_POPUP_TIMEOUT_IN_MS.
    const { code, state } = await new Promise<{ code: string; state: string }>((resolve, reject) => {
        const channel = new BroadcastChannel("autodesk_oauth_callback")
        const timeoutId = setTimeout(() => {
            channel.close()
            reject(new Error("Autodesk authentication timed out"))
        }, 900000)

        channel.onmessage = (event: MessageEvent<AutodeskOAuthCallbackMessage>) => {
            if (event.data?.type !== "autodesk_oauth_callback") return
            clearTimeout(timeoutId)
            channel.close()
            if (event.data.code && event.data.state) {
                resolve({ code: event.data.code, state: event.data.state })
            } else {
                reject(new Error(event.data.error_description || event.data.error || "Authentication failed"))
            }
        }
    })

    if (state !== stateIn) {
        throw new Error("OAuth state mismatch")
    }

    // Exchange the authorization code for tokens using the SDK's internal method.
    // This reads the PKCE code verifier from localStorage, calls Autodesk's token
    // endpoint, and returns the access token directly.  We use this return value
    // rather than getAccessTokenSilently() to guarantee we're returning the token
    // from the current PKCE exchange rather than a potentially stale cached token.
    const accessToken = await client._loginWithPopup({ code, state }, redirectUri, loginOptions)

    // Retrieve the OIDC ID token from the SDK cache.  It was stored there during the
    // PKCE token exchange above and is guaranteed by the OIDC spec to include the
    // 'sub' claim (Autodesk user ID) plus profile fields when the email/profile scopes
    // are present.  Sending it to the backend avoids the need to call /userinfo for
    // user identification.  This call is best-effort — if the cache is unavailable the
    // backend falls back to /userinfo.
    let idToken: string | null = null
    try {
        idToken = await client.getIDTokenSilently()
    } catch {
        // Non-fatal: the backend will fall back to /userinfo to identify the user.
    }

    return { accessToken, idToken }
}
